import React from "react";
import {
    Tabs, Col, Row,
} from "antd";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {checkTranslation} from "@data-force/app";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ReactMarkdown from "react-markdown";
import StoreState from "../../../interfaces/store/storeState";
import {TaskPhaseStats} from "../../../interfaces/store/actions/payload/task";
import "./TaskDetails.scss";
import {getFormattedTime, getJobPhaseStatus} from "../../../utils/common";
import {JobEntity} from "../../../interfaces/store/actions/payload/job";
import {JobType} from "../../../enums/enums";

interface OwnProps {
    tab: string
}

interface StateProps {
    data: JobEntity
    activeJobType: string
    translate: TranslateFunction
}

type Props = OwnProps & StateProps;

export const TaskDetails: React.FC<Props> = (props: Props): React.ReactElement => {
    dayjs.extend(utc);
    const translationPath: string = "components.JobDrawer.TaskDetails.TaskDetails";
    const translationPathProjectType: string = "enums.ProjectType";
    const allowedJobStatuses: JobType[] = [JobType.IN_PROGRESS, JobType.COMPLETED];

    const {
        data,
        activeJobType,
        translate,
    }: Props = props;
    const {
        projectType = "",
        estimatedTime,
        phaseStats = {},
        dueDate,
        taskCount = 0,
        description = "",
    }: JobEntity = data;
    const {REJECTED = 0, SKIPPED = 0}: TaskPhaseStats = phaseStats;
    const numOfTasks: number = taskCount - SKIPPED;

    const dueDateFormat: string = translate("dateFormat.text") as string;
    const notAssigned: string = translate(`${translationPath}.notAssigned.text`) as string;

    const taskStatus: string = getJobPhaseStatus(data);
    return (
        <Tabs.TabPane {...props} className="df-task-details-container">
            <Row>
                <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.typeLabel.text`}/></Col>
                <Col span={12}>{checkTranslation(`${translationPathProjectType}.${projectType}.text`, projectType, translate)}</Col>
            </Row>
            {!!estimatedTime && (
                <Row>
                    <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.estimatedTimeLabel.text`}/></Col>
                    <Col span={12}>{getFormattedTime(estimatedTime, translate)}</Col>
                </Row>
            )}
            <Row>
                <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.tasksLabel.text`}/></Col>
                <Col span={12}>{numOfTasks}</Col>
            </Row>
            {allowedJobStatuses.includes(activeJobType as JobType) && (
                <Row>
                    <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.rejectedTasksLabel.text`}/></Col>
                    <Col span={12} className={REJECTED ? "df-rejected-tasks" : ""}>{REJECTED}</Col>
                </Row>
            )}
            <Row>
                <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.dueDateLabel.text`}/></Col>
                <Col span={12}>{dueDate ? dayjs(dueDate).format(dueDateFormat) : notAssigned}</Col>
            </Row>
            {allowedJobStatuses.includes(activeJobType as JobType) && (
                <Row>
                    <Col span={12} className="df-column-labels"><Translate id={`${translationPath}.statusLabel.text`}/></Col>
                    <Col span={12}>{translate(`enums.TaskStatus.${taskStatus}.text`)}</Col>
                </Row>
            )}
            <Row>
                <Col span={24} className="df-column-labels"><Translate id={`${translationPath}.description.text`}/></Col>
                <Col className="df-task-details-description">
                    <ReactMarkdown linkTarget="_blank">{description}</ReactMarkdown>
                </Col>
            </Row>
        </Tabs.TabPane>
    );
};

function mapStateToProps(state: StoreState): StateProps {
    return {
        data: state.job.selectedJob || {} as JobEntity,
        activeJobType: state.job.activeJobType,
        translate: getTranslate(state.localize),
    };
}

export default connect(mapStateToProps)(TaskDetails);
